import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  inject,
} from "@angular/core";
import { jobEtaClock, jobEtaCountdown, parseJobEta } from "src/app/services/job-eta.util";

/**
 * Countdown to a background job's promised completion:
 *   "About 4 min left · ETA 11:22 AM IST" → "30s left · …" →
 *   "Taking longer than usual… · was due 11:22 AM IST".
 *
 * Pass the job's `expected_completion_at` as `eta`. Renders `fallback` (or
 * nothing) when there is no usable promise. Ticks once a second on its own, so
 * the host needs no timer and no change detection of its own.
 */
@Component({
  selector: "app-job-eta",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (etaMs != null) {
      {{ countdown }}
      @if (showClock) {
        · {{ overdue ? "was due" : "ETA" }} {{ clock }}
      }
    } @else if (fallback) {
      {{ fallback }}
    }
  `,
  styles: [
    `
      :host {
        display: inline;
      }
    `,
  ],
})
export class JobEtaComponent implements OnDestroy {
  /** The job's `expected_completion_at` (ISO string, epoch ms or Date). */
  @Input() set eta(value: string | number | Date | null | undefined) {
    this.etaMs = parseJobEta(value);
    this.now = Date.now();
    this.syncTimer();
  }
  /** Text to show when there is no promise; empty renders nothing. */
  @Input() fallback = "";
  /** Append the IST clock time ("· ETA 11:22 AM IST"). */
  @Input() showClock = true;

  etaMs: number | null = null;
  private now = Date.now();
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly cdr = inject(ChangeDetectorRef);

  get countdown(): string {
    return this.etaMs == null ? "" : jobEtaCountdown(this.etaMs, this.now);
  }

  get clock(): string {
    return this.etaMs == null ? "" : jobEtaClock(this.etaMs);
  }

  get overdue(): boolean {
    return this.etaMs != null && this.now > this.etaMs;
  }

  private syncTimer(): void {
    if (this.etaMs == null) {
      this.stopTimer();
      return;
    }
    if (this.timer) return;
    this.timer = setInterval(() => {
      this.now = Date.now();
      this.cdr.markForCheck();
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }
}
